// @flow
import * as React from "react";
import {
  Card,
  DownloadWidget,
  Section,
  Separator,
  DownloadFileBundleButton
} from "buba-components";
import {
  USER_TYPE__CUSTOMER,
  USER_TYPE__GUARANTEE_AGENT
} from "buba-components/src/utils/userType";

import { requestAndDownloadFileBundle } from "../../api/actions";
import type { RequestedFiles, UploadedFiles } from "./BankInquiryDocuments";
import styles from "./Documents.scss";
import RequestFile from "./RequestFile";

type DownloadsProps = {
  downloadAction: any => Promise<any>,
  fileRequests: RequestedFiles,
  uploadedFiles: UploadedFiles,
  inquiryProcessId: number,
  onError: number => void,
  refetchFiles: () => Promise<any>,
  addMessage: (string, string) => void
};

const Downloads = ({
  downloadAction,
  fileRequests,
  inquiryProcessId,
  onError,
  refetchFiles,
  uploadedFiles,
  addMessage
}: DownloadsProps) => {
  const uploadedByCustomer = uploadedFiles.filter(
    ({ uploadedBy }) => uploadedBy === USER_TYPE__CUSTOMER
  );

  const uploadedByGuaranteeAgent = uploadedFiles.filter(
    ({ uploadedBy }) => uploadedBy === USER_TYPE__GUARANTEE_AGENT
  );
  const requestedFromCustomer = fileRequests.filter(
    req => req.requestedFrom === USER_TYPE__CUSTOMER
  );
  const requestedFromGuaranteeAgent = fileRequests.filter(
    req => req.requestedFrom === USER_TYPE__GUARANTEE_AGENT
  );

  const shouldDisplayClientUploads = !!uploadedByCustomer.length || !!requestedFromCustomer.length;
  const shouldDisplayGuaranteeAgentUploads =
    !!uploadedByGuaranteeAgent.length || !!requestedFromGuaranteeAgent.length;

  return (
    <Section title="Download-Bereich" numbered reducedMargin>
      <Card>
        {/* Documents from Client */}
        {shouldDisplayClientUploads && (
          <>
            <div className={styles.downloadSectionHeaderContainer}>
              <header>Unterlagen vom Kunden</header>
              {!!uploadedByCustomer.length && (
                <DownloadFileBundleButton
                  downloadBundleAction={requestAndDownloadFileBundle}
                  uploadedFileIds={uploadedByCustomer.map(file => file.id)}
                  addMessage={addMessage}
                />
              )}
            </div>
            <div className={styles.widgetContainer}>
              {uploadedByCustomer.map(file => (
                <DownloadWidget
                  uploadDate={file.createdAt}
                  downloadAction={downloadAction}
                  errorCallback={onError}
                  filename={file.filename}
                  id={Number(file.id)}
                  key={file.id}
                  status={
                    file.downloadedByBankAdvisorAt && file.downloadedByBankAdvisorAt
                      ? "downloaded"
                      : "uploaded"
                  }
                  title={file.description || file.filename}
                  successCallback={refetchFiles}
                />
              ))}

              {requestedFromCustomer.map(file => (
                <DownloadWidget
                  uploadDate={file.createdAt}
                  key={file.id}
                  title={file.description}
                  status="not_uploaded"
                />
              ))}
            </div>
            <Separator />
          </>
        )}

        {/* Documents from BUBA */}
        {shouldDisplayGuaranteeAgentUploads && (
          <>
            <div className={styles.downloadSectionHeaderContainer}>
              <header>Unterlagen von der Bürgschaftsbank</header>
              {!!uploadedByGuaranteeAgent.length && (
                <DownloadFileBundleButton
                  downloadBundleAction={requestAndDownloadFileBundle}
                  uploadedFileIds={uploadedByGuaranteeAgent.map(file => file.id)}
                  addMessage={addMessage}
                />
              )}
            </div>
            <div className={styles.widgetContainer}>
              {uploadedByGuaranteeAgent.map(file => (
                <DownloadWidget
                  uploadDate={file.createdAt}
                  downloadAction={downloadAction}
                  errorCallback={onError}
                  filename={file.filename}
                  id={Number(file.id)}
                  key={file.id}
                  status={file.downloadedByBankAdvisorAt ? "downloaded" : "uploaded"}
                  title={file.filename}
                  successCallback={refetchFiles}
                />
              ))}
              {requestedFromGuaranteeAgent.map(file => (
                <DownloadWidget
                  uploadDate={file.createdAt}
                  key={file.id}
                  title={file.description}
                  status="not_uploaded"
                />
              ))}
            </div>
          </>
        )}

        {/* Request documents from BUBA */}
        <RequestFile
          inquiryProcessId={inquiryProcessId}
          c
          onError={onError}
          refetchFiles={refetchFiles}
        />
      </Card>
    </Section>
  );
};

export default Downloads;
